import { PhoneIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { cn } from "../utils/cn.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";

const GUMMY_RAISED_CARD = cn(
  "rounded-surface-md bg-white dark:bg-mono-900",
  "shadow-card"
);

const GUMMY_CONTACT_PILL = cn(
  "relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full",
  "bg-tile-people text-white",
  "shadow-raised",
  "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
  "motion-reduce:transform-none motion-reduce:transition-none",
  "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
);

type ContactData = {
  id: number | string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
};

type ContactCardProps = {
  contact: ContactData;
  className?: string;
  variant?: "card" | "compact";
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const ContactCard = ({
  contact,
  className,
  variant = "card"
}: ContactCardProps) => {
  const isGummy = useIsGummy();
  const initials = getInitials(contact.name || "??");

  if (variant === "compact") {
    return (
      <div className={cn("flex flex-1 justify-between gap-4", className)}>
        <div className="flex-1">
          <div className="flex justify-between">
            <h5 className="text-xl">{contact.name}</h5>
          </div>
          <div>
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className="text-mono-500 dark:text-mono-400 block"
              >
                {contact.email}
              </a>
            )}
            {contact.phone && (
              <a
                href={`tel:${contact.phone}`}
                className="text-mono-500 dark:text-mono-400 block"
              >
                {contact.phone}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-none items-start gap-2">
          {contact.phone && (
            <button
              onClick={() => {
                window.location.href = `tel:${contact.phone}`;
              }}
              className="border-mono-200 text-mono-500 dark:border-mono-700 dark:bg-mono-800 dark:text-mono-400 flex h-9 w-9 items-center justify-center rounded-full border bg-white transition-all hover:border-green-300 hover:bg-green-50 hover:text-green-600 active:scale-95 dark:hover:border-green-500/50 dark:hover:bg-green-500/10 dark:hover:text-green-400"
              aria-label={`Call ${contact.name}`}
            >
              <PhoneIcon className="h-4 w-4" />
            </button>
          )}
          {contact.email && (
            <button
              onClick={() => {
                window.location.href = `mailto:${contact.email}`;
              }}
              className="border-mono-200 text-mono-500 dark:border-mono-700 dark:bg-mono-800 dark:text-mono-400 flex h-9 w-9 items-center justify-center rounded-full border bg-white transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 active:scale-95 dark:hover:border-blue-500/50 dark:hover:bg-blue-500/10 dark:hover:text-blue-400"
              aria-label={`Email ${contact.name}`}
            >
              <EnvelopeIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isGummy) {
    return (
      <div className={cn("group relative p-6", GUMMY_RAISED_CARD, className)}>
        <div className="flex items-center gap-6">
          <div
            className={cn(
              "size-14 relative shrink-0 items-center justify-center overflow-hidden rounded-full text-base font-black text-white",
              "bg-tile-people",
              "shadow-raised",
              "hidden md:flex"
            )}
            style={{ textShadow: "var(--text-shadow-on-color)" }}
          >
            <span aria-hidden className="top-gloss-large" />
            <span className="relative">{initials}</span>
          </div>

          <div className="min-w-0 flex-1">
            <h5 className="truncate text-xl font-black leading-tight tracking-tight text-mono-900 dark:text-mono-100">
              {contact.name}
            </h5>

            <div className="mt-1 space-y-0.5">
              {contact.email && (
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2 text-sm font-extrabold text-mono-600 dark:text-mono-300"
                >
                  <EnvelopeIcon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{contact.email}</span>
                </a>
              )}
              {contact.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center gap-2 text-sm font-extrabold text-mono-600 dark:text-mono-300"
                >
                  <PhoneIcon className="h-4 w-4 shrink-0" />
                  <span>{contact.phone}</span>
                </a>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {contact.phone && (
              <button
                onClick={() => {
                  window.location.href = `tel:${contact.phone}`;
                }}
                className={cn(
                  GUMMY_CONTACT_PILL,
                  "h-14 w-14 text-sm font-extrabold"
                )}
                aria-label={`Call ${contact.name}`}
                style={{ textShadow: "var(--text-shadow-on-color)" }}
              >
                <span aria-hidden className="top-gloss-large" />
                <PhoneIcon className="relative h-5 w-5" />
              </button>
            )}
            {contact.email && (
              <button
                onClick={() => {
                  window.location.href = `mailto:${contact.email}`;
                }}
                className={cn(
                  GUMMY_CONTACT_PILL,
                  "h-14 w-14 text-sm font-extrabold"
                )}
                aria-label={`Email ${contact.name}`}
                style={{ textShadow: "var(--text-shadow-on-color)" }}
              >
                <span aria-hidden className="top-gloss-large" />
                <EnvelopeIcon className="relative h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "border-mono-200 hover:border-mono-300 border-broder dark:border-border  dark:bg-mono-925 dark:hover:border-mono-600  group relative rounded-xl border bg-white  p-4 transition-all hover:shadow-md",
        className
      )}
    >
      <div className="flex items-start gap-4">
        <div className="size-11 relative flex shrink-0 items-center justify-center overflow-hidden rounded-full  bg-blue-50 text-sm font-semibold  text-blue-500 shadow-sm ring-2 ring-white dark:bg-blue-950 dark:ring-blue-800/50">
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <h5 className="text-mono-900 dark:text-mono-100  truncate text-base">
            {contact.name}
          </h5>

          <div>
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className="text-mono-500 hover:text-mono-700 dark:text-mono-400 dark:hover:text-mono-200 flex items-center gap-2 text-sm transition-colors"
              >
                <EnvelopeIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{contact.email}</span>
              </a>
            )}
            {contact.phone && (
              <a
                href={`tel:${contact.phone}`}
                className="text-mono-500 hover:text-mono-700 dark:text-mono-400 dark:hover:text-mono-200 flex items-center gap-2 text-sm transition-colors"
              >
                <PhoneIcon className="h-3.5 w-3.5 shrink-0" />
                <span>{contact.phone}</span>
              </a>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {contact.phone && (
            <button
              onClick={() => {
                window.location.href = `tel:${contact.phone}`;
              }}
              className="border-mono-200 text-mono-500 dark:border-mono-700 dark:bg-mono-800 dark:text-mono-400 flex h-9 w-9 items-center justify-center rounded-full border bg-white transition-all hover:border-green-300 hover:bg-green-50 hover:text-green-600 active:scale-95 dark:hover:border-green-500/50 dark:hover:bg-green-500/10 dark:hover:text-green-400"
              aria-label={`Call ${contact.name}`}
            >
              <PhoneIcon className="h-4 w-4" />
            </button>
          )}
          {contact.email && (
            <button
              onClick={() => {
                window.location.href = `mailto:${contact.email}`;
              }}
              className="border-mono-200 text-mono-500 dark:border-mono-700 dark:bg-mono-800 dark:text-mono-400 flex h-9 w-9 items-center justify-center rounded-full border bg-white transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 active:scale-95 dark:hover:border-blue-500/50 dark:hover:bg-blue-500/10 dark:hover:text-blue-400"
              aria-label={`Email ${contact.name}`}
            >
              <EnvelopeIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ContactCardSkeleton = () => (
  <div className="border-mono-200 dark:border-mono-700/50 dark:bg-mono-800/50 animate-pulse rounded-xl border bg-white p-4">
    <div className="flex items-start gap-4">
      <div className="bg-mono-200 dark:bg-mono-700 h-12 w-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="bg-mono-200 dark:bg-mono-700 h-5 w-32 rounded" />
        <div className="bg-mono-200 dark:bg-mono-700 h-4 w-48 rounded" />
        <div className="bg-mono-200 dark:bg-mono-700 h-4 w-28 rounded" />
      </div>
      <div className="flex gap-2">
        <div className="bg-mono-200 dark:bg-mono-700 h-9 w-9 rounded-full" />
        <div className="bg-mono-200 dark:bg-mono-700 h-9 w-9 rounded-full" />
      </div>
    </div>
  </div>
);

export { ContactCard, ContactCardSkeleton };
export type { ContactData, ContactCardProps };
